import React, { type ReactNode, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl';
import { MountEvent } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages';

export type Props = {
	isDisabled?: boolean;
	isLoading?: boolean;
	isInitialLoading: boolean;
	tooltipContent: string;
	onMount: (arg1: UIAnalyticsEvent) => void;
	onClick: (arg1: SyntheticEvent<HTMLElement>, arg2: UIAnalyticsEvent) => void;
};

type ViewProps = Props;

const WithTooltip = ({ content, children }: { content: string; children: ReactNode }) => {
	if (content) {
		return (
			<Tooltip tag="span" content={content}>
				{children}
			</Tooltip>
		);
	}
	return <>{children}</>;
};

const ViewWorkflowButton = ({
	isDisabled,
	isLoading,
	isInitialLoading,
	onClick,
	onMount,
	tooltipContent,
}: ViewProps) => {
	const intl = useIntl();
	const isDisabledColor = isDisabled === true && !isInitialLoading;

	return (
		<Wrapper>
			<MountEvent onMount={onMount} />
			<WithTooltip content={tooltipContent}>
				<ButtonWrapper>
					<CustomThemeButton
						testId="issue-field-status.common.ui.view-workflow.button.view-workflow"
						shouldFitContainer
						appearance="subtle"
						spacing="none"
						isDisabled={isDisabled}
						isLoading={isLoading}
						onClick={onClick}
						theme={(currentTheme, theme) => {
							const buttonTheme = currentTheme(theme);
							return {
								...buttonTheme,
								buttonStyles: {
									...buttonTheme.buttonStyles,
									alignItems: 'center',
									textAlign: 'left',
									color: isDisabledColor
										? token('color.text.disabled', colors.N70)
										: token('color.text', colors.N900),
									padding: `0 ${token('space.150', '12px')}`,
								},
							};
						}}
						interactionName="auto-generated.issue-field-status.common.ui.view-workflow.button.view-workflow.button"
					>
						{intl.formatMessage(messages.viewWorkflow)}
					</CustomThemeButton>
				</ButtonWrapper>
			</WithTooltip>
		</Wrapper>
	);
};

export default ViewWorkflowButton;

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Wrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 6}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderTop: `2px solid ${token('color.border', colors.N40)}`,
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ButtonWrapper = styled.div({
	display: 'flex',
	flexGrow: 1,
	height: '100%',
});
